// components/secure/ExaminerScopeModal.js
// The managing broker's scope decision for one inspection account.
//
// A box per area. Ticking one grants it; unticking a live grant revokes it. The
// areas that can never be opened are listed too, greyed and unticked, because the
// useful thing to show a broker under examination is not a short list of what is
// available but the full list with the closed items named and cited — it answers
// "did you withhold anything?" with "yes, these, for this reason".
//
// A request from the examiner arrives as a pre-ticked-but-pending row carrying
// their stated reason. The broker approves it by saving, or denies it outright.
import React, { useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from './Modal';

const TONE = {
    granted: 'bg-green-100 text-green-800',
    requested: 'bg-amber-100 text-amber-800',
    denied: 'bg-red-100 text-red-700',
    revoked: 'bg-slate-200 text-slate-600',
    expired: 'bg-slate-200 text-slate-600',
    not_requested: 'bg-slate-100 text-slate-500',
};
const LABEL = {
    granted: 'granted', requested: 'requested', denied: 'denied',
    revoked: 'revoked', expired: 'expired', not_requested: 'not granted',
};

const ExaminerScopeModal = ({ examiner, areas, onClose, onSaved }) => {
    const api = useApiClient();
    const [checked, setChecked] = useState({});
    const [note, setNote] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const grantable = useMemo(() => (examiner?.areas || []), [examiner]);
    const closed = useMemo(() => (areas || []).filter((a) => !a.is_grantable), [areas]);

    // Start from what is actually live, so saving with no changes changes nothing.
    useEffect(() => {
        const initial = {};
        for (const a of grantable) initial[a.area_key] = a.status === 'granted';
        setChecked(initial);
    }, [grantable]);

    const dirty = grantable.some((a) => !!checked[a.area_key] !== (a.status === 'granted'));

    const save = async () => {
        setSaving(true); setError('');
        try {
            const toGrant = grantable.filter((a) => checked[a.area_key] && a.status !== 'granted');
            const toRevoke = grantable.filter((a) => !checked[a.area_key] && a.status === 'granted');
            // Deny, rather than silently drop, a request the broker has left unticked:
            // the examiner should see an answer, not an unchanging screen.
            const toDeny = grantable.filter((a) => !checked[a.area_key] && a.status === 'requested');

            if (toGrant.length) {
                await api.post('/api/examiner-access/grants', {
                    user_id: examiner.user_id,
                    area_keys: toGrant.map((a) => a.area_key),
                    decision_note: note || null,
                    expires_at: expiresAt || null,
                });
            }
            for (const a of [...toRevoke, ...toDeny]) {
                await api.put(`/api/examiner-access/grants/${a.grant_id}`, {
                    status: a.status === 'granted' ? 'revoked' : 'denied',
                    decision_note: note || null,
                });
            }
            onSaved();
        } catch (e) {
            setError(e.message || 'Could not save the scope.');
        } finally { setSaving(false); }
    };

    const input = 'block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-slate-500 focus:border-slate-500';

    return (
        <Modal open size="lg" onClose={onClose}
            title={`Inspection scope — ${examiner?.username || ''}`}
            footer={<>
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
                <button onClick={save} disabled={saving || !dirty}
                    className="px-4 py-2 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300">
                    {saving ? 'Saving…' : 'Save scope'}
                </button>
            </>}>
            <p className="text-sm text-slate-600 mb-4">
                This account reads <strong>nothing</strong> until an area is ticked below. Every area is
                read-only whether ticked or not, and access ends on the expiry date if one is set.
            </p>

            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

            <div className="space-y-2">
                {grantable.map((a) => (
                    <label key={a.area_key}
                        className="flex gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" className="mt-1 h-4 w-4"
                            checked={!!checked[a.area_key]}
                            onChange={(e) => setChecked({ ...checked, [a.area_key]: e.target.checked })} />
                        <span className="flex-1">
                            <span className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">{a.title}</span>
                                <span className={`px-2 py-0.5 rounded text-xs ${TONE[a.status] || TONE.not_requested}`}>
                                    {LABEL[a.status] || a.status}
                                </span>
                            </span>
                            {a.description && <span className="block text-xs text-slate-500 mt-0.5">{a.description}</span>}
                            {a.statutory_ref && <span className="block text-xs text-slate-400 mt-0.5">{a.statutory_ref}</span>}
                            {a.status === 'requested' && a.requested_note && (
                                <span className="block text-xs text-amber-800 mt-1">
                                    Requested: “{a.requested_note}”
                                </span>
                            )}
                            {a.expires_at && (
                                <span className="block text-xs text-slate-500 mt-0.5">
                                    Expires {new Date(a.expires_at).toLocaleDateString()}
                                </span>
                            )}
                        </span>
                    </label>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Access ends (optional)</label>
                    <input type="date" className={input} value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Note on this decision</label>
                    <input className={input} value={note} placeholder="e.g. 2026 escrow examination"
                        onChange={(e) => setNote(e.target.value)} />
                </div>
            </div>

            {closed.length > 0 && (
                <div className="mt-5 pt-4 border-t border-slate-200">
                    <p className="text-xs font-semibold uppercase text-slate-500 mb-2">
                        Closed to inspection accounts — cannot be opened here
                    </p>
                    <ul className="space-y-1">
                        {closed.map((a) => (
                            <li key={a.area_key} className="text-xs text-slate-500">
                                <span className="font-medium text-slate-600">{a.title}</span>
                                {a.description ? ` — ${a.description}` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Modal>
    );
};

export default ExaminerScopeModal;
